import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { motion } from "motion/react";
import { useAuth } from "../auth.tsx";
import { useAction } from "../api.ts";
import { Button, ErrorText, Field, Input } from "../ui/index.tsx";
import { AuthLayout } from "../layouts.tsx";
import { authItem, PasswordInput } from "./Login.tsx";
import { EASE } from "./member/kit.tsx";

const SEGMENTS = 4;

/** Advice only: the server enforces length, nothing else. Returns 0 (empty) to 4 filled segments. */
function strength(pw: string): { level: number; label: string } {
  if (!pw) return { level: 0, label: "" };
  if (pw.length < 8) return { level: 1, label: "Too short" };
  const points = 1 + Number(pw.length >= 12) + Number(/[a-z]/.test(pw) && /[A-Z]/.test(pw)) + Number(/\d/.test(pw)) + Number(/[^A-Za-z0-9]/.test(pw));
  if (points <= 2) return { level: 1, label: "Weak" };
  if (points === 3) return { level: 2, label: "Okay" };
  if (points === 4) return { level: 3, label: "Strong" };
  return { level: 4, label: "Very strong" };
}

// Full class strings so Tailwind can see them.
const FILL = ["", "bg-bad", "bg-warn", "bg-ok", "bg-ok"];
const TEXT = ["text-faint", "text-bad", "text-warn", "text-ok", "text-ok"];

export function StrengthMeter({ password }: { password: string }) {
  const { level, label } = strength(password);
  return (
    <div className="mt-2">
      <div className="flex gap-1.5" aria-hidden>
        {Array.from({ length: SEGMENTS }, (_, i) => (
          <div key={i} className="h-1 flex-1 overflow-hidden rounded-full bg-line">
            <motion.div className={`h-full origin-left rounded-full transition-colors duration-300 ${FILL[level]}`}
              initial={false} animate={{ scaleX: i < level ? 1 : 0 }} transition={{ duration: 0.3, ease: EASE }} />
          </div>
        ))}
      </div>
      {/* Fixed height so the label appearing doesn't shift the form. */}
      <div className={`mt-1 h-4 text-xs ${TEXT[level]}`} role="status">{label && `Strength: ${label}`}</div>
    </div>
  );
}

export default function Register() {
  const { me, register } = useAuth();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [referral, setReferral] = useState(params.get("ref") ?? "");
  const [mismatch, setMismatch] = useState(false);

  const submit = useAction(async () => { await register(username.trim(), password, referral.trim(), email.trim()); });

  const next = params.get("next");
  if (me) return <Navigate to={next?.startsWith("/") && !next.startsWith("//") ? next : "/home"} replace />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setMismatch(true); return; }
    setMismatch(false);
    void submit.run();
  };

  return (
    <AuthLayout title="Create your account" subtitle="Takes a few seconds. No email needed."
      footer={<>Already have an account? <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="text-accent hover:underline">Sign in</Link></>}>
      <form onSubmit={onSubmit} className="space-y-4">
        <motion.div variants={authItem}>
          <Field label="Username" hint="3 to 32 characters: letters, numbers, . _ -">
            <Input autoComplete="username" autoFocus required minLength={3} maxLength={32} pattern="[A-Za-z0-9_.\-]{3,32}" value={username} onChange={(e) => setUsername(e.target.value)} />
          </Field>
        </motion.div>
        <motion.div variants={authItem}>
          <Field label="Password" hint="8 to 128 characters">
            <PasswordInput label="Password" autoComplete="new-password" required minLength={8} maxLength={128} value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <StrengthMeter password={password} />
        </motion.div>
        <motion.div variants={authItem}>
          <Field label="Confirm password" error={mismatch ? "Passwords don't match" : null}>
            <PasswordInput label="Confirm password" autoComplete="new-password" required minLength={8} maxLength={128} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
        </motion.div>
        <motion.div variants={authItem}>
          <Field label="Email (optional)" hint="Only used to recover your account if you forget your password.">
            <Input type="email" autoComplete="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </motion.div>
        <motion.div variants={authItem}>
          <Field label="Referral code (optional)">
            <Input value={referral} onChange={(e) => setReferral(e.target.value)} className="font-mono" />
          </Field>
        </motion.div>
        <div role="alert" className="empty:hidden"><ErrorText>{submit.error}</ErrorText></div>
        <motion.div variants={authItem}>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={submit.pending}>Create account</Button>
        </motion.div>
      </form>
    </AuthLayout>
  );
}
